import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { compact, concat, sortBy, uniq } from 'lodash';

import answerLanguagesResource from 'src/actions/answers/answerLanguagesResource';
import availableLanguagesResource from 'src/actions/answers/availableLanguagesResource';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useResource from 'src/presentation/hooks/useResource';

const dataAidPrefix = 'answer-language-';
const description =
  'Select the language that you would like to add. Please note that languages cannot be removed once they are added.';

const AnswerLanguageEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const formContext = useForm({
    add: answerLanguagesResource.effects.add,
    defaultItem: { code: null },
    errors,
    item,
    resetErrors: answerLanguagesResource.actions.resetErrors,
    root,
    update: answerLanguagesResource.effects.update,
  });
  const { workingState, updateState, onClose } = formContext;

  const languages = useGetUnusedAnswerLanguages();
  const onLanguageChange = useCallback(
    code => {
      updateState({ code });
    },
    [updateState]
  );

  const onSave = useOnSave({ ...formContext, replace: answerLanguagesResource.effects.replace });

  return (
    <Form context={formContext}>
      <H1>Add Answer Language</H1>

      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section>
          <Row>
            <Dropdown
              autoFocus
              data-aid={`${dataAidPrefix}editor-languages-dropdown`}
              description={description}
              label="Language"
              onChange={onLanguageChange}
              options={languages}
              placeholder="Select Language"
              value={workingState.code}
            />
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const useGetUnusedAnswerLanguages = () => {
  const { loadingState: usedLangLoadingState, value: usedLangs } = useResourceValue(answerLanguagesResource);
  const { loadingState: availableLangLoadingState, values: availableLangs } = useResource(availableLanguagesResource);

  const isLoaded = usedLangLoadingState === 'succeeded' && availableLangLoadingState === 'succeeded';

  return useMemo(
    () => {
      const usedLanguages = new Set(usedLangs);
      const languages: { label: string; value: string }[] = [];
      availableLangs.forEach(({ id }) => {
        if (!usedLanguages.has(id)) languages.push({ label: Languages[id], value: id });
      });
      return sortBy(languages, ['label']);
    },
    // don't update language list after languages have loaded
    // no error if same language is added by multiple users
    // eslint-disable-next-line
    [isLoaded]
  );
};

const { resetErrors, validationError } = answerLanguagesResource.actions;

function useOnSave({ replace, workingState }) {
  const dispatch = useDispatch();
  const { value } = useResourceValue(answerLanguagesResource);
  return useCallback(() => {
    dispatch(resetErrors({}));
    if (!workingState.code) {
      dispatch(validationError([{ code: 'blank' }]));
      return;
    }
    const langs = compact(uniq(concat(value, workingState.code)));
    dispatch(replace({}, langs));
  }, [dispatch, replace, value, workingState]);
}

export default AnswerLanguageEditor;
